<template>
  <div id="sample-order-details-view" class="vx-row">
    <div class="vx-col w-full">
      <vx-card v-if="dataLoaded" :title="orderDetails.sample_order_code">
        <template slot="actions">
          <div class="flex items-center">
            <vs-chip transparent :color="chargeTypeColor" class="mr-3 font-semibold" style="border-radius: 0">
              {{ chargeTypeLabel }}
            </vs-chip>
            <div class="flex">
              <vs-button v-if="['Requested'].includes(orderDetails.status.name)"
                @click.stop="onClickSampleOrderUpdate()"
                         color="warning" type="border">Update Order Details
              </vs-button>
              <vx-tooltip
                v-if="['Requested'].includes(orderDetails.status.name)"
                :text="isProformaWithIssuedInvoice ? 'Cannot edit items: an invoice has already been issued for this proforma order' : ''"
                :disabled="!isProformaWithIssuedInvoice"
                class="ml-2 inline-block">
                <vs-button
                  @click="onClickOpenUpdateOrderItems"
                  :class="{ 'opacity-50 cursor-not-allowed': isProformaWithIssuedInvoice }"
                  color="warning"
                  type="border">
                  Update Order Items
                </vs-button>
              </vx-tooltip>
              <vs-button @click="onClickSamplePdf" icon-pack="feather" icon="icon-file" class="ml-2" color="primary" type="border">Sample Delivery Note</vs-button>
              <vs-button @click="onClickBack" class="ml-2" color="primary" type="filled">Back to orders</vs-button>
            </div>
          </div>
        </template>

        <div class="vx-row md:mb-3 mt-8">
          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="UserIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md">
                                <span v-if="orderDetails.customer">
                                  <h5 class="mb-2">Customer</h5>
                                  <span class="cursor-pointer text-primary" @click.stop="onClickViewCustomer">
                                    {{ orderDetails.customer.company_name || '' }}
                                  </span>
                                </span>
                                <span v-else>
                                  <h5 class="mb-2">Customer Lead</h5>
                                  <span class="cursor-pointer text-primary" @click.stop="onClickViewCustomerLead">
                                    {{ orderDetails.customer_lead.company_name || orderDetails.customer_lead.code }}
                                  </span>    
                                </span>
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="DollarSignIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <span v-if="orderDetails.customer">
                                  <h5 class="mb-2">Account Number</h5>
                                  {{ orderDetails.customer.account_number }}
                                </span>
                                <span v-else>
                                  <h5 class="mb-2">Lead Account Number</h5>
                                  {{ orderDetails.customer_lead.code }}
                                </span>
                            </span>
              </vs-col>
            </vs-row>
          </div>

          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="CalendarIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Order Date</h5>
                                {{ __dateTimeFormatter(orderDetails.created_at) }}
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="RefreshCwIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Order Status</h5>
                                <vs-chip style="border-radius: 0" class="text-center" transparent
                                         :color="sampleOrderStatusColorFactory()">
                                    {{ orderDetails.status.name }}
                                </vs-chip>
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="UserIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Created By</h5>
                                <span>{{ orderDetails.created_by || 'N/A' }}</span>
                            </span>
              </vs-col>
            </vs-row>
          </div>
        </div>

        <div class="vx-row md:mb-3 mt-16">
          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="MapPinIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md">
                                <h5 class="mb-2">Address</h5>
                                {{ orderDetails.name }} <br v-if="orderDetails.name">
                                {{ orderDetails.line1 }} <br v-if="orderDetails.line1">
                                {{ orderDetails.line2 }} <br v-if="orderDetails.line2">
                                {{ orderDetails.line3 }} <br v-if="orderDetails.line3">
                                {{ orderDetails.town_state }} <br>
                                {{ orderDetails.post_code }} <br>
                                {{ orderDetails.country }} <br>
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="TruckIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Delivery Instructions</h5>
                                <vx-read-more :text="orderDetails.delivery_instruction" :length="100"/>
                            </span>
              </vs-col>
            </vs-row>
          </div>

          <div class="vx-col md:w-1/5 w-full">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="InfoIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Notes</h5>
                                {{ orderDetails.notes || 'N/A' }}
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full" >
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="SquareIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Customer PO Number</h5>
                                {{ orderDetails.customers_order_number || 'N/A' }}
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full" v-if="orderDetails.customer && orderDetails.sale_quote.quote_code">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="InfoIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Sales Quote</h5>
                                <span class="text-primary" @click.stop="onClickSalesQuote">
                                  {{ orderDetails.sale_quote.quote_code || 'N/A' }}
                                </span>
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full" v-if="orderDetails.shipping_details && orderDetails.shipping_details.length > 0">
            <vs-row vs-type="flex">
              <vs-col
                vs-type="flex" vs-justify="center" vs-align="start" vs-w="2">
                <feather-icon icon="BoxIcon" class="text-success cursor-pointer"/>
              </vs-col>
              <vs-col
                vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                            <span class="text-md break-all">
                                <h5 class="mb-2">Shipping Details</h5>
                                <span class="">
                                  <p>Shipping Name: {{ orderDetails.shipping_details[0].name }}</p>
                                  <p>Booking Date: {{ orderDetails.shipping_details[0].booking_date }}</p>
                                  <p>Ref No: {{ orderDetails.shipping_details[0].reference_no }}</p>
                                  <p>Expected Date: {{ orderDetails.shipping_details[0].expected_date }}</p>
                                </span>
                            </span>
              </vs-col>
            </vs-row>
          </div>
          <div class="vx-col md:w-1/5 w-full" v-if="[2, 5].includes(orderDetails.status.id)">
             <vs-row vs-type="flex">
               <vs-col vs-type="flex" vs-justify="start" vs-align="start" vs-w="10">
                <span class="text-md break-all w-full">
                    <h5 class="mb-2">Feedback</h5>
                    <vs-textarea v-model="feedback" class="w-full" placeholder="Enter feedback" />
                    <vs-button @click="onClickSaveFeedback" class="mt-2" color="primary" type="border" size="small">Save Feedback</vs-button>
                </span>
              </vs-col>
            </vs-row>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full">
            <div class="flex float-right">
              <div class="con-btns-user flex items-center justify-between">
                <div class="flex mb-4">
                  <vs-button v-if="[1].includes(orderDetails.status.id)"
                             @click="onClickStatusChangeConfirm('dispatch', orderDetails.id)" type="border" size="small"
                             icon-pack="feather" icon="icon-log-in" color="warning" class="mr-2">Dispatch Order
                  </vs-button>
                  <vs-button v-if="[1].includes(orderDetails.status.id)"
                             @click="onClickStatusChangeConfirm('refuse', orderDetails.id)" type="border" size="small"
                             icon-pack="feather" icon="icon-alert-triangle" color="danger" class="mr-2">Refuse Order
                  </vs-button>
                  <vs-button v-if="[1].includes(orderDetails.status.id)"
                             @click="onClickStatusChangeConfirm('cancel', orderDetails.id)" type="border" size="small"
                             icon-pack="feather" icon="icon-x" color="danger" class="mr-2">Cancel Order
                  </vs-button>
                  <vs-button v-if="[2].includes(orderDetails.status.id)"
                             @click="onClickStatusChangeConfirm('deliver', orderDetails.id)" type="border" size="small"
                             icon-pack="feather" icon="icon-check" color="primary" class="mr-2">Deliver Order
                  </vs-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="printDiv" class="vx-row my-8">
          <div class="vx-col w-full">

            <vs-table :data="orderDetails.formulation_items" stripe>
              <template slot="header">
                <h4 class="mb-2">
                  Sample items
                </h4>
              </template>
              <template slot="thead">
                <vs-th width="15%">FORMULATION CODE</vs-th>
                <vs-th width="25%">FORMULATION NAME</vs-th>
                <vs-th width="10%">QUANTITY (GRAMS)</vs-th>
                <vs-th width="10%">PRICE PER UNIT (GBP/KG)</vs-th>
                <vs-th width="10%" v-if="!__isCurrencyGbp(account_currency)">PRICE PER UNIT ({{account_currency}}/KG)</vs-th>
                <vs-th width="10%">NET VALUE (GBP)</vs-th>
                <vs-th width="10%" v-if="!__isCurrencyGbp(account_currency)">NET VALUE ({{ account_currency }})</vs-th>
                <vs-th width="10%">ACTIONS</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr :ref="`table_row_${indextr}`" :key="indextr" v-for="(tr, indextr) in data">

                  <vs-td :data="data[indextr].formulation_code">
                    <HyperLink :id="data[indextr].formulation_id" type="formulation">{{ data[indextr].formulation_code }}</HyperLink>
                  </vs-td>

                  <vs-td :data="data[indextr].name">
                    <HyperLink :id="data[indextr].formulation_id" type="formulation">{{ data[indextr].name }}</HyperLink>
                  </vs-td>

                  <vs-td :data="data[indextr].quantity">
                    {{ data[indextr].quantity }}
                  </vs-td>

                  <vs-td :data="data[indextr].price">
                    {{ Number(data[indextr].price).toFixed(2) }}
                  </vs-td>

                  <vs-td :data="data[indextr].price" v-if="!__isCurrencyGbp(account_currency)">
                    {{ Number(data[indextr].price) | format_price(orderDetails.fx_value) }}
                  </vs-td>

                  <vs-td :data="data[indextr].price">
                    {{ calculateNetValue(data[indextr]) }}
                  </vs-td>

                  <vs-td :data="data[indextr].price" v-if="!__isCurrencyGbp(account_currency)">
                    {{ calculateNetValue(data[indextr], orderDetails.fx_value) }}
                  </vs-td>

                  <vs-td :data="data[indextr].formulation_code">
                    <div class="vx-row">
                      <vx-tooltip class="ml-2" text="Print label" position="left">
                        <vs-button @click.stop="printLabelOpenPopUp(data[indextr])"
                                   size="small" color="warning" type="filled" icon-pack="feather" icon="icon-printer"></vs-button>
                      </vx-tooltip>
                    </div>
                  </vs-td>


                  <!-- <vs-td>
                    <vx-tooltip class="" text="View formulation breakdown" position="left">
                      <vs-button
                        size="small" color="primary" type="border" icon-pack="feather"
                        icon="icon-git-merge"></vs-button>
                    </vx-tooltip>
                  </vs-td> -->

                  <!-- <template class="expand-user" slot="expand">
                    <div class="con-expand-users w-4/5">
                      <vs-table class="mt-8 mb-8" v-if="tr.raw_material_items.length > 0" :data="tr.raw_material_items">

                        <template slot="header">
                          <h5 class="mb-4">Formulations breakdown</h5>
                        </template>

                        <template slot="thead">
                          <vs-th width="10%">PRODUCT CODE</vs-th>
                          <vs-th width="15%">PRODUCT NAME</vs-th>
                          <vs-th width="10%">QUANTITY (GRAMS)</vs-th>
                          <vs-th width="5%">SOL</vs-th>
                          <vs-th width="5%">HALAL</vs-th>
                          <vs-th width="5%">KOSHER</vs-th>
                          <vs-th width="10%">STATUS</vs-th>
                        </template>

                        <template slot-scope="{data}">
                          <vs-tr :key="index_rm_item" v-for="(tr_item, index_rm_item) in data">

                            <vs-td>
                              <span v-if="tr_item.company_product_code"> {{ tr_item.category }}-{{ tr_item.company_product_code }} </span>
                              <span v-if="tr_item.formulation_code"> {{ tr_item.formulation_code }} </span>
                            </vs-td>

                            <vs-td :data="tr_item.company_product_name">
                              {{ tr_item.company_product_name || '' }}
                            </vs-td>

                            <vs-td :data="tr_item.quantity_kg">
                              {{ Number(tr_item.quantity_kg).toFixed(1) }}
                            </vs-td>

                            <vs-td :data="tr_item.dissolvable">
                              {{ tr_item.dissolvable === 'Yes' ? 'S' : '' }}
                            </vs-td>

                            <vs-td :data="tr_item.halal_suitable">
                              {{ suitableOrCertified(tr_item.halal_suitable, tr_item.halal_certified) }}
                            </vs-td>

                            <vs-td :data="tr_item.kosher_suitable">
                              {{ suitableOrCertified(tr_item.kosher_suitable, tr_item.kosher_certified) }}
                            </vs-td>

                            <vs-td :data="tr_item.status">
                              {{ tr_item.status }}
                            </vs-td>

                          </vs-tr>
                        </template>

                      </vs-table>


                    </div>
                  </template> -->

                </vs-tr>
              </template>
            </vs-table>
            <div class="float-right mt-4">
              <div class="flex">
                <vs-button @click.stop="onClickSendSpecToCustomers()" v-if="orderDetails.status.id > 1"
                           class="ml-2"
                           color="warning" type="border">Send/Resend product spec to users
                </vs-button>
                <vx-tooltip text="VIEW PRODUCT SPEC SEND LOG" position="top" class="mr-2">
                  <vs-button @click.stop="onClickViewSendSpecLogs()" v-if="orderDetails.status.id > 1"
                            class="ml-2" icon-pack="feather" icon="icon-book"
                            color="warning" type="border"></vs-button>
                </vx-tooltip>
                <vs-button @click.stop="onClickCreateSalesQuote" v-if="!!orderDetails.customer"
                           class="ml-2"
                           color="success" type="border">Create sales quote
                </vs-button>
                <vs-button @click.stop="onClickGeneratePdf"
                           class="ml-2"
                           color="primary" type="border">Print sample order
                </vs-button>
              </div>
            </div>

          </div>
        </div>
        <SampleOrderInvoiceList :customer_id="orderDetails.customer ? orderDetails.customer.id : (orderDetails.customer_lead ? orderDetails.customer_lead.customer_id : (orderDetails.customer_id || null))" :customer_lead="orderDetails.customer_lead" :account_currency="account_currency" :fx_value="fx_value" :order_code="orderDetails.sample_order_code" :dataLoaded = "is_invoice_data_loaded" :invoice_list="invoice_list"  :account_type="accountCreditType" @refreshData="refreshData()"/>
      </vx-card>
    </div>
    <update-customer-sample-order-pop-up ref="updateCustomerSampleOrderPopUp"/>
    <!-- <generate-pdf-sample-order-pop-up ref="generatePdfSampleOrderPopUp"/> -->
    <update-customer-sample-order-items-pop-up ref="UpdateCustomerSampleOrderItemsPopUp"/>
    <sample-delivery-note-pdf-pop-up ref="sampleDeliveryNotePdfPopUp"/>

    <vs-prompt
      title="Shipping Details"
      @cancel="clearShippingDetails"
      @accept="submitShippingDetails"
      @close="activePrompt = false"
      :is-valid="validShippingDetails"
      acceptText="Submit"
      :active.sync="activePrompt">

      <div class="con-exemple-prompt">
        Enter shipping details to <b>continue</b>.

      <div class="vx-row mt-4">
        <div class="vx-col w-full">
          <label class="text-sm">Booking Date</label>
          <flat-pickr
            class="w-full"
            :config="{ dateFormat: 'd/m/Y'}"
            v-model="shipping_details_obj.booking_date"
            placeholder="Choose Date"
          />
        </div>
      </div>

      <div class="vx-row mt-2">
        <div class="vx-col w-full">
          <label class="text-sm">Courier Name</label>
          <vs-input v-model="shipping_details_obj.name" class="w-full" />
        </div>
      </div>

      <div class="vx-row mt-2">
        <div class="vx-col w-full">
          <label class="text-sm">Ref Number</label>
          <vs-input v-model="shipping_details_obj.reference_no" class="w-full" />
        </div>
      </div>

      <div class="vx-row mt-2">
        <div class="vx-col w-full">
          <label class="text-sm">Expected Date</label>
          <flat-pickr
            class="w-full"
            :config="{ dateFormat: 'd/m/Y'}"
            v-model="shipping_details_obj.expected_date"
            placeholder="Choose Date"
          />
        </div>
      </div>

      <vx-card noShadow cardBorder class="my-4">
        <div class="vx-row mb-3 mt-4">
          <div class="vx-col w-full">
            <div class="vx-row mb-2">
              <div class="vx-col w-3/5">FG code</div>
              <div class="vx-col w-2/5">Specification available in FG</div>
            </div>
            <div class="vx-row mb-1" v-for="(fg_item, fg_item_index) in send_spec_details_obj.formulation_items" :key="fg_item_index">
              <div class="vx-col w-3/5">
                {{ fg_item.formulation_code }}
              </div>
              <div class="vx-col w-2/5" :class="fg_item.is_available_to_send ? 'text-primary' : 'text-danger'">
                {{ fg_item.is_available_to_send ? 'Yes' : 'No' }}
              </div>
          </div>
        </div>
      </div>

      <div class="vx-row mt-2" v-if="orderDetails.recipients.length > 0">
        <div class="vx-col w-full">
          <label class="text-sm">Send specification</label>
          <vs-switch v-model="stored_details[2]" class="mb-4" :disabled="!send_spec_details_obj.formulation_items.some((x) => x.is_send)"></vs-switch>
          <span>
            Recipient emails: 
            <p v-for="(recipient, index) in orderDetails.recipients" :key="index">{{ index + 1 }}) {{ recipient.email }}</p>
          </span>
        </div>
      </div>
      </vx-card>

      </div>
    </vs-prompt>

    <vs-prompt
      :is-valid="send_spec_details_obj.formulation_items.some((x) => x.is_send)"
      class="send_spec_prompt"
      title="Send product specs to users"
      @cancel="clearSendSpecDetailsObj"
      @accept="sendProductSpecs"
      @close="clearSendSpecDetailsObj"
      acceptText="Send"
      :active.sync="activeSendSpectPrompt">
        <div class="vx-row mb-3">
          <div class="vx-col w-full">
            <label class="text-sm">Send to pre selected users below?</label>
            <vs-switch v-model="send_spec_details_obj.is_all_recipients" class="mb-4" ></vs-switch>
            <span v-if="send_spec_details_obj.is_all_recipients">
              Recipient emails: 
              <p v-for="(recipient, index) in orderDetails.recipients" :key="index">{{ index + 1 }}) {{ recipient.email }}</p>
            </span>
          </div>
        </div>
        <div class="vx-row mb-3" v-if="!send_spec_details_obj.is_all_recipients">
          <div class="vx-col w-full">
            <label class="text-sm">Recipient emails</label>
            <v-select multiple :clearable="true" placeholder=""  v-model="send_spec_details_obj.recipients"
                     :options="email_list" :dir="$vs.rtl ? 'rtl' : 'ltr'" :reduce="option => option" label="email"></v-select>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col w-full">
            <div class="vx-row mb-2 text-primary">
              <div class="vx-col w-1/3">FG code</div>
              <div class="vx-col w-1/3">Specification available in FG</div>
              <div class="vx-col w-1/3">Send</div>
            </div>
            <div class="vx-row mb-1" v-for="(fg_item, fg_item_index) in send_spec_details_obj.formulation_items" :key="fg_item_index">
              <div class="vx-col w-1/3">
                {{ fg_item.formulation_code }}
              </div>
              <div class="vx-col w-1/3">
                {{ fg_item.is_available_to_send ? 'Yes' : 'No' }}
              </div>
              <div class="vx-col w-1/3">
               <vs-switch v-on:input="onChangeSendSwitch" v-if="fg_item.is_available_to_send" v-model="send_spec_details_obj.formulation_items[fg_item_index].is_send"></vs-switch>
              </div>
          </div>
          </div>
        </div>
    </vs-prompt>
    <ViewSampleSpecEmailLogPopUp ref="viewSampleSpecEmailLogPopUp"/>
    <SampleOrderItemPrintLabelPopUp ref="sampleOrderItemPrintLabelPopUp"/>
  </div>
</template>

<script>
import {RepositoryFactory} from '@/repository/RepositoryFactory'

const CustomerRepository = RepositoryFactory.get('customer')
import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'
import Calculations from '@/mixins/Calculations'
import UpdateCustomerSampleOrderPopUp from '@/components/popups/customer/sample-orders/UpdateCustomerSampleOrderPopUp'
import GeneratePdfSampleOrderPopUp from "@/components/popups/customer/sample-orders/GeneratePdfSampleOrderPopUp";
import $ from 'jquery'
import router from "@/router";
import UpdateCustomerSampleOrderItemsPopUp from "@/components/popups/customer/sample-orders/UpdateCustomerSampleOrderItemsPopUp";
import SampleDeliveryNotePdfPopUp from '@/components/popups/customer/sample-orders/SampleDeliveryNotePdfPopUp.vue'

import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
const DropDownRepository = RepositoryFactory.get('drop_down')
const CrmRepository = RepositoryFactory.get('crm');
const FormulationsRepository = RepositoryFactory.get('formulations');
import vSelect from 'vue-select'
import ViewSampleSpecEmailLogPopUp from '@/components/popups/customer/sample-orders/ViewSampleSpecEmailLogPopUp.vue'
import SampleOrderInvoiceList from '@/views/pages/sample-orders/SampleOrderInvoiceList.vue'
import SampleOrderItemPrintLabelPopUp from '../../../components/popups/customer/sample-orders/SampleOrderItemPrintLabelPopUp.vue'

export default {
  name: "SampleOrderDetailsView",
  components: {
    UpdateCustomerSampleOrderItemsPopUp,
    UpdateCustomerSampleOrderPopUp,
    GeneratePdfSampleOrderPopUp,
    flatPickr,
    SampleDeliveryNotePdfPopUp,
    'v-select': vSelect,
    ViewSampleSpecEmailLogPopUp,
    SampleOrderInvoiceList,
    SampleOrderItemPrintLabelPopUp
  },
  data: function () {
    return {
      orderDetails: {
        is_chargeable: null,
        recipients: [],
        customer: {
          account_currency: null
        },
        fx_value: 1,
        customer_lead: {
          account_currency: null
        }
      },
      id: '',
      dataLoaded: false,
      shipping_details_obj: {
        booking_date: '',
        name: '',
        reference_no: '',
        expected_date: ''
      },
      stored_details: [],
      activePrompt: false,
      email_list: [],
      activeSendSpectPrompt: false,
      send_spec_details_obj: {
        is_all_recipients: true,
        recipients: [],
        formulation_items: []
      },
      feedback: '',
      account_currency: '',
      fx_value: 1,
      invoice_list: [],
      is_invoice_data_loaded: false
    }
  },
  computed: {
    validShippingDetails() {
      return true
    },
    accountCreditType() {
      return this.orderDetails.customer &&
            this.orderDetails.customer.account_credit
          ? this.orderDetails.customer.account_credit.account_type
          : 'proforma'
    },
    isChargeableOrder() {
      if (this.orderDetails.is_free_of_charge === true || this.orderDetails.is_free_of_charge == 1) {
        return false
      }
      return !!(this.orderDetails.is_chargeable == 1 || this.orderDetails.is_chargeable === true)
    },
    chargeTypeLabel() {
      return this.isChargeableOrder ? 'Chargeable' : 'Free of Charge'
    },
    chargeTypeColor() {
      return this.isChargeableOrder ? 'success' : 'warning'
    },
    hasActiveSampleInvoice() {
      if (!this.invoice_list || !this.invoice_list.length) {
        return false
      }
      return this.invoice_list.some(inv => {
        const statusName = inv.status ? inv.status.name : ''
        return statusName.toLowerCase() !== 'cancelled'
      })
    },
    isProformaWithIssuedInvoice() {
      return this.accountCreditType === 'proforma' && this.hasActiveSampleInvoice
    }
  },
  mixins: [SuccessMessage, ErrorMessages, Calculations],
  async mounted() {
    this.id = this.$route.params.id
    await this.getSampleOrderDetails()
    await this.fetchInvoiceList()
  },
  methods: {
    onClickBack() {
      if (this.$route.query.back_customer) {
        this.$router.push({path: `/pages/manage-nimble/customers/${this.$route.query.back_customer}?step=7`})
      } else {
        this.$router.push({path: `/pages/sample-orders`})
      }
    },
    async refreshData() {
      this.getSampleOrderDetailsView()
    },
    async onClickOpenUpdateOrderItems(){
      if (this.isProformaWithIssuedInvoice) {
        this.$vs.dialog({
          color: 'danger',
          title: 'Action Not Allowed',
          text: 'This is a proforma account and an invoice has already been issued for this order. To change products, please cancel the invoice or create a new order and request Accounts to issue a credit note.',
          accept: null,
          acceptText: 'Close'
        })
        return
      }
      const customerId = this.orderDetails.customer ? this.orderDetails.customer.id : this.orderDetails.customer_lead.id;
      await this.$refs.UpdateCustomerSampleOrderItemsPopUp.openPopup(this.id, customerId, this.orderDetails.formulation_items, this.isProformaWithIssuedInvoice)
    },
    onClickStatusChangeConfirm(status, id) {
        if (
            status === 'dispatch' &&
            this.accountCreditType === 'proforma' && this.isChargeableOrder &&
            !this.isIncludedPaidInvoice()
        ) {
            this.$vs.dialog({
                color: 'danger',
                title: 'Warning',
                text: 'This is a proforma account and there are unpaid invoices against this order. The order cannot be accepted until all invoices have been paid.',
                accept: null,
                acceptText: 'Close'
            })
            return
        }
        this.$vs.dialog({
            type: 'confirm',
            color: 'primary',
            title: 'Confirm',
            text: 'Are you sure you want to make this change?',
            accept: this.onChangeSampleOrderStatus,
            parameters: [status, id]
        })
    },
    isIncludedPaidInvoice() {
      if (!this.invoice_list.length || !this.orderDetails.formulation_items.length) {
          return false
      }
      const paidQuantities = {}
      this.invoice_list
          .filter(invoice => invoice.status.name === 'Paid')
          .forEach(invoice => {
              invoice.items.forEach(item => {
                  if (!paidQuantities[item.formulation_code]) {
                      paidQuantities[item.formulation_code] = 0
                  }

                  paidQuantities[item.formulation_code] += Number(item.quantity)
              })
          })
      return this.orderDetails.formulation_items.every(item => {
          const paidQuantity = paidQuantities[item.formulation_code] || 0

          return paidQuantity >= Number(item.quantity)
      })
    },
    printLabelOpenPopUp(dataSet){
      this.$refs.sampleOrderItemPrintLabelPopUp.openPopUp(dataSet)
    },
    calculateNetValue(item, fx_rate = 1) {
      const quantityInKg = Number(item.quantity) / 1000;
      const unitPrice = Number(this.$options.filters.format_price(item.price, fx_rate));
      return (quantityInKg * unitPrice).toFixed(2);
    },
    async onChangeSampleOrderStatus(params, is_initial = true) {
      if(params[0] === 'dispatch' && is_initial) {
        this.stored_details = params
        this.send_spec_details_obj.formulation_items = this.orderDetails.formulation_items
        await this.fetchSpecAvailability()
        this.stored_details[2] = this.send_spec_details_obj.formulation_items.some((x) => x.is_send)
        this.activePrompt = true
        return null
      }
      await this.$vs.loading({
        container: '#sample-order-details-view',
        scale: 0.45
      })
      try {
        await CustomerRepository.changeSampleOrderStatus(params[0], params[1], params[2])
        this.showSuccessMessage('Status change successfully!')
        await this.getSampleOrderDetails()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close('#sample-order-details-view > .con-vs-loading')
    },
    async getSampleOrderDetails() {
      await this.$vs.loading({
        container: '#sample-order-details-view',
        scale: 0.45
      })
      try {
        const {data} = (await CustomerRepository.getSampleOrder(this.id)).data
        this.orderDetails = data
        this.feedback = data.feedback || ''

        this.orderDetails.formulation_items = this.orderDetails.formulation_items.map((x) => {
          if(x.formulation_items && x.formulation_items.length > 0) {
            x.raw_material_items =  x.raw_material_items.concat(x.formulation_items)
          }
          return x
        })
        // this.$refs.table_row_1.clicktd()
        this.account_currency = data.customer ? data.customer.account_currency : ( data.customer_lead ? data.customer_lead.account_currency : 'GBP')
        this.fx_value = data.fx_value
      } catch (e) {
        console.log(e.message)
        this.convertAndNotifyError(e)
      }
      this.$vs.loading.close('#sample-order-details-view > .con-vs-loading')
      this.dataLoaded = true
    },
    async fetchInvoiceList() {
        try {
            this.is_invoice_data_loaded = false
            const { data } = await CustomerRepository.getSampleOrderInvoices(this.id)
            this.invoice_list = data.data
            this.invoice_list.forEach(invoice => {
                invoice.items.forEach(item => {
                    item.price = Number(item.price)
                })
            })
            this.is_invoice_data_loaded = true
        } catch (e) {
            this.showWarningMessage('Something went wrong. Please try again!')
        }
    },
    async getSampleOrderDetailsView() {
      await this.getSampleOrderDetails()
      await this.fetchInvoiceList()
    },
    suitableOrCertified(suitable, certified) {
      let text = ''
      if (suitable === 'Yes') {
        text = text + ' S'
      }
      if (certified === 'Yes') {
        text = text + ' C'
      }
      return text
    },

    async onClickSampleOrderUpdate() {
      await this.$refs.updateCustomerSampleOrderPopUp.openPopup(this.orderDetails.id)
    },
    async onClickSamplePdf() {
      await this.$refs.sampleDeliveryNotePdfPopUp.openPopup(this.orderDetails, this.account_currency, this.fx_value)
    },

    sampleOrderStatusColorFactory() {
      const colors = {
        1: '#f8961e',
        2: '#f9844a',
        3: '#f9c74f',
        4: '#43aa8b',
        5: '#219ebc',
        6: '#006d77',
        7: '#d62828',
        8: '#4895ef',
        9: '#3a0ca3',
        10: '#52b788',
        11: '#f72585'
      }
      return colors[this.orderDetails.status.id]
    },
    async onClickGeneratePdf() {
      let routeData = this.$router.resolve({name: 'sample-orders-print-view', params: {id: this.orderDetails.id }});
      window.open(routeData.href, '_blank');
    },
    async onClickCreateSalesQuote() {
      if(this.orderDetails.sale_quote.quote_code) {
        this.open = false
        this.$vs.dialog({
          type: 'confirm',
          color: 'warning',
          title: 'Note',
          acceptText: 'Proceed',
          text: 'This sample already has a sales quote attached to it.\n' +
            'If you create another quote, this will remove the original\n' +
            'quote link to this sample. Do you wish to proceed?',
          accept: this.onClickSalesQuoteCreateConfirm,
        })
      } else {
        this.onClickSalesQuoteCreateConfirm()
      }
    },
    onClickSalesQuoteCreateConfirm() {
      router.push(`/pages/sales-quote?customer_id=${this.orderDetails.customer.id}&sample_order_id=${this.id}`)
    },
    onClickPrint() {
      let printContents = document.getElementById('printDiv').innerHTML;
      let originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
    },
    viewFormulation(item){
      router.push(`/pages/formulations/${item.formulation_id}`)
    },
    onClickSalesQuote() {
      router.push(`/pages/sales-quote?id=${this.orderDetails.sale_quote.id}`)
    },
    onClickViewCustomer() {
      router.push(`/pages/manage-nimble/customers/${this.orderDetails.customer.id}`)
    },
    onClickViewCustomerLead() {
      switch (this.orderDetails.customer_lead.status) {
        case 'Lead':
          router.push(`/pages/crm/marketing-leads-suspect?search=${this.orderDetails.customer_lead.code}`);
          break;
        case 'Subscriber':
          router.push(`/pages/crm/marketing-leads-influence?search=${this.orderDetails.customer_lead.code}`);
          break;
        case 'Pending':
          router.push(`/pages/crm/marketing-leads?search=${this.orderDetails.customer_lead.code}`);
          break;
      }
    },
    clearShippingDetails() {
      this.shipping_details_obj =  {
        booking_date: '',
        name: '',
        reference_no: '',
        expected_date: ''
      }
    },
    async submitShippingDetails() {
      try {
        await CustomerRepository.updateSampleOrderShippingDetails(this.stored_details[1], { shipping_details : [ this.shipping_details_obj ]})
        await this.onChangeSampleOrderStatus(this.stored_details, false)
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    async onClickSendSpecToCustomers() {
      try {
        // fetch customer users
        if(this.orderDetails.customer) {
          const { data } = (await DropDownRepository.getCustomerUsersDropdownList(this.orderDetails.customer.id)).data
          this.email_list = data.map((x) => { return { email: x.email, id: x.id, type: 'customer' } })
        }
        // fetch lead email for customer lead
        if(this.orderDetails.customer_lead && !this.orderDetails.customer_lead.customer_id) {
          const { data } = (await CrmRepository.getMerketingLeadDetails(this.orderDetails.customer_lead.id)).data
          this.email_list = [ { email: data.email, id: data.id, type: 'lead' } ]
        }
        // fetch customer users for converted from lead
        if(this.orderDetails.customer_lead && this.orderDetails.customer_lead.customer_id) {
          const { data } = (await DropDownRepository.getCustomerUsersDropdownList(this.orderDetails.customer_lead.customer_id)).data
          this.email_list = data.map((x) => { return { email: x.email, id: x.id, type: 'customer' } })
        }
        this.send_spec_details_obj.formulation_items = this.orderDetails.formulation_items
        await this.fetchSpecAvailability()
        this.activeSendSpectPrompt = true
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    async onClickViewSendSpecLogs() {
      await this.$refs.viewSampleSpecEmailLogPopUp.openPopup(this.id, this.orderDetails.sample_order_code)
    },
    async fetchSpecAvailability() {
      try {
        const { data } = ( await FormulationsRepository.checkSpectOfProduct( { formulations: this.send_spec_details_obj.formulation_items.map((x) => x.formulation_code ) } ) ).data
        this.send_spec_details_obj.formulation_items.map((x) => {
          x.is_available_to_send = data[x.formulation_code]
          x.is_send = x.is_available_to_send ? true : false
          return x
        })
      } catch (error) {
        this.convertAndNotifyError(e)
      }
    },
    clearSendSpecDetailsObj() {
      this.send_spec_details_obj = {
        is_all_recipients : true,
        recipients: [],
        formulation_items: [],
      }
      this.activeSendSpectPrompt = false
    },
    onChangeSendSwitch() {
      this.activeSendSpectPrompt = !this.activeSendSpectPrompt
      this.activeSendSpectPrompt = !this.activeSendSpectPrompt
    },
    async onClickSaveFeedback() {
      try {
        await CustomerRepository.updateSampleOrderFeedback(this.orderDetails.id, { feedback: this.feedback })
        this.showSuccessMessage('Feedback saved successfully!')
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    async sendProductSpecs() {
      try {
        if(!this.send_spec_details_obj.formulation_items.some((x) => x.is_send)) return 
        let payload = {
          is_all_recipients: this.send_spec_details_obj.is_all_recipients,
          recipients: this.send_spec_details_obj.is_all_recipients ? [] : this.send_spec_details_obj.recipients.map((x) =>{ return { id: x.id, type: x.type } }),
          formulation_ids: this.send_spec_details_obj.formulation_items.filter((x) => x.is_send).map((x) => x.formulation_id)
        }
        await CustomerRepository.sendProductSpecToUsers(this.orderDetails.id, payload)
        this.clearSendSpecDetailsObj()
        this.showSuccessMessage('Product specifications sent successfully!')
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
  }
}
</script>

<style scoped>
  .send_spec_prompt>>>.vs-dialog {
    /* min-height: 50% !important; */
    min-width: 40% !important;
  }
</style>




